import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Cake, KeyRound, PartyPopper, Search, Users } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { SetPinDialog } from '@/components/production/PeopleTab';
import { useProfiles } from '@/features/users/hooks';
import { useAllHrInfo, type EmployeeHrInfo } from '@/features/hr/hooks';
import {
  celebrationPhrase,
  labelOf,
  EMPLOYMENT_STATUSES,
  upcomingCelebrations,
} from '@/features/hr/model';
import { useCredentialStatus } from '@/features/workMode/hooks';
import { useWorkLookups } from '@/features/workMode/useLookups';
import type { WorkPerson } from '@/features/workMode/useLookups';
import { formatDateOnly } from '@/lib/formatters';
import type { Tables } from '@/types/database';

/**
 * HR → Employees. The directory managers use to open a person's record
 * and to set a Work Mode PIN for someone who does not have one yet.
 */
export function EmployeeInformationPage() {
  const navigate = useNavigate();
  const { data: profiles, isLoading: profilesLoading } = useProfiles();
  const hrQuery = useAllHrInfo();
  const hrRows = hrQuery.data;
  const hrLoading = hrQuery.isLoading;
  const { data: credentials } = useCredentialStatus();
  const lookups = useWorkLookups();
  const [query, setQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [pinFor, setPinFor] = useState<WorkPerson | null>(null);

  const hrByProfile = useMemo(() => {
    const map = new Map<string, EmployeeHrInfo>();
    for (const row of hrRows ?? []) map.set(row.profile_id, row);
    return map;
  }, [hrRows]);

  const people = useMemo(() => {
    return (profiles ?? [])
      .filter((p) => p.role !== 'external_lender')
      .filter((p) => showInactive || p.is_active)
      .map((p) => {
        const hr = hrByProfile.get(p.id) ?? null;
        const name = p.full_name || p.email;
        const preferred = hr?.preferred_name?.trim();
        return {
          profile: p,
          hr,
          displayName: preferred || name,
          legalName: preferred && preferred !== name ? name : null,
          title: p.job_title,
          position: hr?.position ?? null,
          department: hr?.department ?? null,
          startDate: hr?.start_date ?? p.start_date,
          status: hr?.employment_status ?? 'active',
        };
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [profiles, hrByProfile, showInactive]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return people;
    return people.filter((person) => {
      const hay = [
        person.displayName,
        person.legalName,
        person.profile.email,
        person.title,
        person.position,
        person.department,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [people, query]);

  const celebrations = useMemo(
    () =>
      upcomingCelebrations(
        people
          .filter((p) => p.profile.is_active)
          .map((p) => ({
            profileId: p.profile.id,
            name: p.displayName,
            dateOfBirth: p.profile.date_of_birth,
            startDate: p.startDate,
            celebrateBirthday: p.hr?.celebrate_birthday ?? true,
          })),
      ),
    [people],
  );

  const missingPins = people.filter(
    (p) => p.profile.is_active && !credentials?.get(p.profile.id)?.has_pin,
  ).length;
  const isLoading = profilesLoading || hrLoading;

  if (hrQuery.isError) {
    return (
      <EmptyState
        icon={Users}
        title="Employee records are unavailable"
        detail={
          hrQuery.error instanceof Error
            ? hrQuery.error.message
            : 'Apply the latest database migration and try again.'
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="HR"
        title="Employees"
      />

      {!isLoading && celebrations.length > 0 && (
        <Card>
          <CardContent className="flex flex-col gap-2 p-4 sm:flex-row sm:items-start sm:gap-3">
            <PartyPopper className="mt-0.5 h-4 w-4 shrink-0 text-info" />
            <div>
              <p className="text-sm font-medium text-foreground">Coming up</p>
              <ul className="mt-1 space-y-1">
                {celebrations.map((item) => (
                  <li key={`${item.kind}-${item.profileId}-${item.date}`}>
                    <Link
                      to={`/hr/employees/${item.profileId}`}
                      className="text-sm text-info hover:underline"
                    >
                      {item.kind === 'birthday' ? (
                        <Cake className="mr-1 inline h-3.5 w-3.5" />
                      ) : null}
                      {celebrationPhrase(item)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, title, department"
            className="pl-9"
            aria-label="Search employees"
          />
        </div>
        <div className="flex items-center gap-3">
          {credentials && missingPins > 0 && (
            <p className="text-sm text-muted-foreground">
              {missingPins === 1
                ? '1 person still needs a PIN'
                : `${missingPins} people still need a PIN`}
            </p>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowInactive((v) => !v)}
          >
            {showInactive ? 'Hide inactive' : 'Show inactive'}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No one matches"
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Person</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Start</TableHead>
                <TableHead>PIN</TableHead>
                <TableHead aria-label="Actions" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((person) => {
                const cred = credentials?.get(person.profile.id);
                const locked = !!cred?.locked_until && Date.parse(cred.locked_until) > Date.now();
                return (
                  <TableRow
                    key={person.profile.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/hr/employees/${person.profile.id}`)}
                  >
                    <TableCell>
                      <PersonCell
                        person={person.profile}
                        displayName={person.displayName}
                        legalName={person.legalName}
                      />
                      {!person.profile.is_active && (
                        <Badge variant="muted" className="ml-11 mt-1">
                          Inactive
                        </Badge>
                      )}
                      {person.status !== 'active' && (
                        <span className="ml-11 mt-1 block text-xs text-muted-foreground">
                          {labelOf(EMPLOYMENT_STATUSES, person.status)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-foreground">{person.title || '—'}</p>
                      {person.position && (
                        <p className="text-xs text-muted-foreground">{person.position}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {person.department || '—'}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {person.startDate ? formatDateOnly(person.startDate) : '—'}
                    </TableCell>
                    <TableCell>
                      {!credentials ? (
                        '—'
                      ) : locked ? (
                        <Badge variant="warning-soft">Locked</Badge>
                      ) : cred?.has_pin ? (
                        <Badge variant="success-soft">Set</Badge>
                      ) : (
                        <Badge variant="muted">None</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPinFor(lookups.person(person.profile.id));
                        }}
                      >
                        <KeyRound className="h-3.5 w-3.5" />
                        {cred?.has_pin ? 'Reset PIN' : 'Set PIN'}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <SetPinDialog person={pinFor} onOpenChange={(open) => !open && setPinFor(null)} />
    </div>
  );
}

function PersonCell({
  person,
  displayName,
  legalName,
}: {
  person: Tables<'profiles'>;
  displayName: string;
  legalName: string | null;
}) {
  return (
    <div className="flex items-center gap-3">
      <Avatar className="h-8 w-8">
        {person.avatar_url && <AvatarImage src={person.avatar_url} alt="" />}
        <AvatarFallback>{initials(displayName, person.email)}</AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-foreground">{displayName}</p>
        <p className="truncate text-xs text-muted-foreground">{legalName ?? person.email}</p>
      </div>
    </div>
  );
}

function initials(name: string, email: string): string {
  return (name || email)
    .split(/[.\s@]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('');
}
